// Live location: blue dot with heading cone, accuracy ring, and follow mode.
import { Marker } from 'maplibre-gl';
import { circlePolygon, inBbox } from '../lib/geo.js';
import { smoother } from './heading.js';

const EMPTY = { type: 'FeatureCollection', features: [] };

export function createLocation(map, { bbox, onPosition, onStatus }) {
  const el = document.createElement('div');
  el.className = 'user-dot';
  el.innerHTML = '<div class="user-cone"></div><div class="user-core"></div>'; // static markup
  el.dataset.heading = 'none';
  const marker = new Marker({ element: el, rotationAlignment: 'map' });

  let watchId = null;
  let last = null;
  let follow = false;
  let placed = false;
  const smooth = smoother();

  // A fix can arrive before the style has loaded (permission already granted at startup).
  let styleReady = map.loaded();
  if (!styleReady) map.once('load', () => { styleReady = true; drawAccuracy(); });

  function drawAccuracy() {
    if (!styleReady || !last) return;
    if (!map.getSource('accuracy')) {
      const below = map.getLayer('site-clusters') ? 'site-clusters' : undefined;
      map.addSource('accuracy', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'accuracy-fill', type: 'fill', source: 'accuracy', paint: { 'fill-color': '#1a73e8', 'fill-opacity': 0.12 } }, below);
      map.addLayer({ id: 'accuracy-line', type: 'line', source: 'accuracy', paint: { 'line-color': '#1a73e8', 'line-opacity': 0.35, 'line-width': 1 } }, below);
    }
    map.getSource('accuracy').setData(circlePolygon(last.lat, last.lon, Math.max(last.accuracy || 0, 5)));
  }

  function setFollow(on) {
    follow = on;
    onStatus?.({ active: watchId != null, follow, inArea: last?.inArea ?? null });
    if (on && last?.inArea) map.easeTo({ center: [last.lon, last.lat], zoom: Math.max(map.getZoom(), 16.5), duration: 600 });
  }

  // A drag by the person (not our own easeTo) turns follow mode off.
  map.on('dragstart', (e) => { if (e.originalEvent && follow) setFollow(false); });

  function update({ lat, lon, accuracy, heading }) {
    const inArea = inBbox(bbox, lat, lon);
    last = { lat, lon, accuracy, inArea };
    marker.setLngLat([lon, lat]);
    if (!placed) { marker.addTo(map); placed = true; }
    drawAccuracy();
    // GPS course is a fallback heading while moving when there is no compass.
    if (el.dataset.heading !== 'compass' && Number.isFinite(heading)) setHeading(heading, 'course');
    if (follow && inArea) map.easeTo({ center: [lon, lat], duration: 800 });
    onPosition?.(last);
  }

  function setHeading(deg, source = 'compass') {
    const h = smooth(deg);
    el.dataset.heading = source;
    el.style.setProperty('--heading', `${h}deg`);
  }

  function start() {
    if (!('geolocation' in navigator)) {
      onStatus?.({ error: 'unsupported' });
      return;
    }
    if (watchId != null) { setFollow(true); return; }
    let first = true;
    watchId = navigator.geolocation.watchPosition(
      (p) => {
        update({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy, heading: p.coords.heading });
        if (first) { first = false; setFollow(last.inArea); }
      },
      (err) => {
        onStatus?.({ error: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable', active: watchId != null, follow });
        if (err.code === err.PERMISSION_DENIED) { navigator.geolocation.clearWatch(watchId); watchId = null; }
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    onStatus?.({ active: true, follow, inArea: null });
  }

  return {
    start,
    setFollow,
    setHeading,
    get position() { return last; },
    get following() { return follow; },
    get active() { return watchId != null; },
    /** Dev/testing: feed a position as if it came from GPS. */
    simulate(lat, lon, accuracy = 10, heading) {
      const firstFix = watchId == null;
      if (firstFix) watchId = -1;
      update({ lat, lon, accuracy, heading });
      if (firstFix) setFollow(last.inArea);
    },
  };
}
